// ================================================
// CLARITY REPORTING — PRICING PAGE
// ================================================

import React from 'react';
import { Link } from 'react-router-dom';

const PLANS = [
  {
    id: 'site_owner',
    name: 'Site Owner',
    price: '$20',
    period: '/month',
    description: 'For business owners who want to see their real rankings independently.',
    features: [
      '1 website tracked',
      'Up to 3 competitor sites',
      'Weekly keyword report',
      'Position change alerts (problems & concerns)',
      'Growth opportunity analysis',
      'Google Search Console integration',
      'Email delivery on your schedule',
    ],
    notIncluded: [
      'White label reports',
      'Client management',
      'Multiple websites',
    ],
    cta: 'Start as Site Owner',
    highlighted: false,
  },
  {
    id: 'agency',
    name: 'Agency',
    price: '$99',
    period: '/month',
    description: 'For agencies that want to provide independent reporting to clients.',
    features: [
      'Up to 20 client websites',
      'Up to 3 competitors per site',
      'Weekly keyword reports for all clients',
      'Position change alerts',
      'Growth opportunity analysis',
      'Google Search Console integration',
      'White label reports with your logo',
      'Reports sent directly to clients',
      'Client management dashboard',
    ],
    notIncluded: [
      'Unlimited clients',
    ],
    cta: 'Start as Agency',
    highlighted: true,
  },
  {
    id: 'agency_pro',
    name: 'Agency Pro',
    price: '$199',
    period: '/month',
    description: 'For larger agencies with unlimited clients and full white label.',
    features: [
      'Unlimited client websites',
      'Up to 3 competitors per site',
      'Weekly keyword reports for all clients',
      'Position change alerts',
      'Growth opportunity analysis',
      'Google Search Console integration',
      'White label reports with your logo & colors',
      'Reports sent directly to clients',
      'Client management dashboard',
      'Priority support',
    ],
    notIncluded: [],
    cta: 'Start as Agency Pro',
    highlighted: false,
  }
];

function CheckIcon() {
  return <span style={{ color: '#1a7a1a', fontWeight: 700, marginRight: 8 }}>✓</span>;
}
function XIcon() {
  return <span style={{ color: '#ccc', marginRight: 8 }}>✗</span>;
}

export default function PricingPage() {
  return (
    <div style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#1a1a1a', minHeight: '100vh' }}>

      {/* Nav */}
      <nav style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '24px 60px', borderBottom: '1px solid #e0e0e0'
      }}>
        <Link to="/" style={{
          fontSize: 13, fontWeight: 700, letterSpacing: 3,
          textTransform: 'uppercase', textDecoration: 'none', color: '#1a1a1a'
        }}>
          Clarity<span style={{ fontWeight: 300, opacity: 0.5 }}> Reporting</span>
        </Link>
        <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
          <Link to="/login" style={{ fontSize: 13, color: '#1a1a1a', textDecoration: 'none' }}>Sign In</Link>
          <Link to="/register" className="btn btn-primary" style={{ padding: '8px 20px' }}>Get Started</Link>
        </div>
      </nav>

      {/* Header */}
      <section style={{ padding: '72px 60px 60px', textAlign: 'center', maxWidth: 800, margin: '0 auto' }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: '#888', marginBottom: 16 }}>
          Simple Pricing
        </div>
        <h1 style={{ fontSize: 44, fontWeight: 700, letterSpacing: -1, marginBottom: 20 }}>
          No long-term contracts.
        </h1>
        <p style={{ fontSize: 17, color: '#666', lineHeight: 1.7 }}>
          Pay month-to-month. Cancel any time. No setup fees. All plans include a full first report
          so you can see exactly what you're getting before your first billing cycle.
        </p>
      </section>

      {/* Plans */}
      <section style={{ padding: '0 60px 80px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0, border: '2px solid #1a1a1a' }}>
          {PLANS.map((plan, i) => (
            <div key={plan.id} style={{
              padding: '40px 36px',
              borderRight: i < 2 ? '1px solid #e0e0e0' : 'none',
              background: plan.highlighted ? '#1a1a1a' : '#fff',
              color: plan.highlighted ? '#fff' : '#1a1a1a',
              position: 'relative'
            }}>
              {plan.highlighted && (
                <div style={{
                  position: 'absolute', top: -1, left: 0, right: 0,
                  textAlign: 'center', padding: '5px',
                  background: '#FFE066', color: '#1a1a1a',
                  fontSize: 10, fontWeight: 700, letterSpacing: 2,
                  textTransform: 'uppercase'
                }}>
                  Most Popular
                </div>
              )}
              {plan.highlighted && <div style={{ marginTop: 24 }} />}

              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: 2,
                textTransform: 'uppercase',
                color: plan.highlighted ? 'rgba(255,255,255,0.5)' : '#888',
                marginBottom: 8
              }}>
                {plan.name}
              </div>

              <div style={{ marginBottom: 20 }}>
                <span style={{ fontSize: 48, fontWeight: 700, letterSpacing: -2 }}>{plan.price}</span>
                <span style={{ fontSize: 14, color: plan.highlighted ? 'rgba(255,255,255,0.5)' : '#888' }}>
                  {plan.period}
                </span>
              </div>

              <p style={{
                fontSize: 14, lineHeight: 1.6, marginBottom: 32,
                color: plan.highlighted ? 'rgba(255,255,255,0.7)' : '#666'
              }}>
                {plan.description}
              </p>

              <Link
                to={`/register?plan=${plan.id}`}
                style={{
                  display: 'block', padding: '12px', textAlign: 'center',
                  fontSize: 12, fontWeight: 700, letterSpacing: 1,
                  textTransform: 'uppercase', textDecoration: 'none',
                  marginBottom: 36,
                  background: plan.highlighted ? '#fff' : '#1a1a1a',
                  color:      plan.highlighted ? '#1a1a1a' : '#fff',
                }}
              >
                {plan.cta}
              </Link>

              <div style={{
                borderTop: `1px solid ${plan.highlighted ? 'rgba(255,255,255,0.15)' : '#e0e0e0'}`,
                paddingTop: 24
              }}>
                {plan.features.map(f => (
                  <div key={f} style={{ fontSize: 13, marginBottom: 10, display: 'flex', alignItems: 'flex-start' }}>
                    <CheckIcon />
                    <span style={{ color: plan.highlighted ? 'rgba(255,255,255,0.85)' : '#444' }}>{f}</span>
                  </div>
                ))}
                {plan.notIncluded.map(f => (
                  <div key={f} style={{ fontSize: 13, marginBottom: 10, display: 'flex', alignItems: 'flex-start' }}>
                    <XIcon />
                    <span style={{ color: plan.highlighted ? 'rgba(255,255,255,0.35)' : '#bbb' }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: '60px', background: '#fafafa', borderTop: '1px solid #e0e0e0' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 48 }}>Common Questions</h2>
          {[
            {
              q: 'Can I use this if I already have an SEO agency?',
              a: 'Yes — that\'s exactly who this is for. Clarity Reporting gives you independent data so you can verify what your agency tells you. Most of our customers use both simultaneously.'
            },
            {
              q: 'Is Google Search Console required?',
              a: 'No. GSC integration is optional but recommended. Without it, we pull rankings from DataForSEO, which covers 99% of keywords. With GSC connected, you also get impressions, CTR, and direct Google data.'
            },
            {
              q: 'How often are reports sent?',
              a: 'Once per week, delivered on the day and time you choose. You can also trigger a manual report at any time from your dashboard.'
            },
            {
              q: 'What is the Agency white label feature?',
              a: 'Agency and Agency Pro customers can upload their logo and set their brand colors. Reports sent to clients show your branding, not Clarity Reporting\'s. Clients receive professional reports that look like they\'re from your firm.'
            },
            {
              q: 'Can I cancel anytime?',
              a: 'Yes. Cancel from your settings page at any time. There are no cancellation fees, and you retain access until the end of your billing period.'
            }
          ].map(faq => (
            <div key={faq.q} style={{ marginBottom: 36, paddingBottom: 36, borderBottom: '1px solid #e0e0e0' }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>{faq.q}</h3>
              <p style={{ fontSize: 14, color: '#555', lineHeight: 1.7 }}>{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: '28px 60px', borderTop: '1px solid #e0e0e0',
        display: 'flex', justifyContent: 'space-between',
        fontSize: 12, color: '#888'
      }}>
        <Link to="/" style={{ color: '#888', textDecoration: 'none', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', fontSize: 11 }}>
          Clarity Reporting
        </Link>
        <div>© {new Date().getFullYear()} Clarity Reporting</div>
      </footer>
    </div>
  );
}
